const ROUTE_LABEL_MAX_LENGTH = 72;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ROUTE_ID_SEPARATOR = '--';

function slugifyRouteLabel(value?: string | null) {
  if (!value) {
    return '';
  }

  const slug = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

  if (slug.length <= ROUTE_LABEL_MAX_LENGTH) {
    return slug;
  }

  const trimmed = slug.slice(0, ROUTE_LABEL_MAX_LENGTH).replace(/-+$/g, '');
  const lastSeparator = trimmed.lastIndexOf('-');
  return lastSeparator >= 32 ? trimmed.slice(0, lastSeparator) : trimmed;
}

function buildReadableRouteParam(id: string, label?: string | null) {
  const trimmedID = id.trim();
  const slug = slugifyRouteLabel(label);
  if (!trimmedID || !slug) {
    return trimmedID;
  }
  return `${slug}${ROUTE_ID_SEPARATOR}${trimmedID}`;
}

export function getRouteEntityID(value?: string | string[] | null) {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) {
    return '';
  }

  if (UUID_PATTERN.test(raw)) {
    return raw;
  }

  const separatorIndex = raw.lastIndexOf(ROUTE_ID_SEPARATOR);
  if (separatorIndex >= 0) {
    const candidate = raw.slice(separatorIndex + ROUTE_ID_SEPARATOR.length);
    if (UUID_PATTERN.test(candidate)) {
      return candidate;
    }
  }

  const suffixMatch = raw.match(
    /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i
  );
  if (suffixMatch) {
    return suffixMatch[1];
  }

  return raw;
}

export function getEventPath(eventID: string, title?: string | null) {
  return `/events/${buildReadableRouteParam(eventID, title)}`;
}

export function buildEventLink(
  eventID: string,
  title?: string | null,
  params: Record<string, string> = {}
) {
  const query = new URLSearchParams(params).toString();
  return `https://radious.app${getEventPath(eventID, title)}${query ? `?${query}` : ''}`;
}

export function buildProfileLink(username: string) {
  return `https://radious.app/profiles/${username}`;
}

type VenueLinkOptions = {
  slug?: string | null;
  name?: string | null;
};

function getVenueRouteParam(venueID: string, options?: VenueLinkOptions) {
  return buildReadableRouteParam(venueID, options?.slug ?? options?.name);
}

export function getVenuePath(venueID: string, options?: VenueLinkOptions) {
  return `/venues/${getVenueRouteParam(venueID, options)}`;
}

export function buildVenueLink(venueID: string, options?: VenueLinkOptions) {
  return `https://radious.app${getVenuePath(venueID, options)}`;
}

type VenueOwnedPublicIdentity = {
  username: string;
  is_venue: boolean;
  venue_id?: string | null;
  venue_name?: string | null;
};

export function buildPublicIdentityLink(identity: VenueOwnedPublicIdentity) {
  if (identity.is_venue && identity.venue_id) {
    return buildVenueLink(identity.venue_id, { name: identity.venue_name });
  }
  return buildProfileLink(identity.username);
}

export function getProfileHref(username: string, params: Record<string, string> = {}): any {
  return {
    pathname: '/profiles/[username]',
    params: {
      username,
      ...params,
    },
  };
}

export function getEventHref(eventID: string, title?: string | null, params: Record<string, string> = {}): any {
  return {
    pathname: '/events/[id]',
    params: {
      id: buildReadableRouteParam(eventID, title),
      ...params,
    },
  };
}

export function getVenueHref(id: string, params: Record<string, string> = {}, options?: VenueLinkOptions): any {
  return {
    pathname: '/venues/[id]',
    params: {
      id: getVenueRouteParam(id, options),
      ...params,
    },
  };
}

export function getPublicIdentityHref(
  identity: VenueOwnedPublicIdentity,
  params: Record<string, string> = {}
): any {
  if (identity.is_venue && identity.venue_id) {
    return getVenueHref(identity.venue_id, params, { name: identity.venue_name });
  }
  return getProfileHref(identity.username, params);
}
